import React, { useEffect, useRef, useState, useCallback } from 'react';
import { createMap } from '../services/mapService';
import ScaleBar from './ScaleBar';
import MapControls from './MapControls';
import MapLeftControls from './MapLeftControls';
import RightClickPopup from './RightClickPopup';
import LongPressPopup from './LongPressPopup';
import CopyrightNotice from './CopyrightNotice';
import Search from './Search';
import SearchResult from './SearchResult';
import { calculateZoomKeepCenter } from '../utils/calcZoomLevel';
import config from '../config/config';
import NaviComponent from './navi/naviComponent';
import SaveSpot from './SaveSpot';

// 検索結果およびマーカーの最大表示数
const MAX_RESULTS = config.search.searchMaxResults;
// 長押しタイマー判定時間(ms)
const LONG_PRESS_TIMER = config.map.longPressTimerMs;
// 地図操作中フラグをリセットするまでの時間(ms)
const MOVING_RESET_DELAY_MS = config.map.movingResetDelayMs;
// 画面ロード時のローディング進捗
const LOADING_PROGRESS_COMPLETE = config.map.loadingProgressComplete;
// idle検知後、100％表示を一瞬見せてから非表示にするための遅延(ms)
const LOADING_HIDE_DELAY_MS = config.map.loadingHideDelayMs;

// ポップアップのオフセット
const POPUP_OFFSET = config.map.popupOffset;

// flyToで移動する際のduration
const FLYTO_DURATION_MS = config.map.animationDurationMs;
// 遠距離検索と近距離検索の境界値（メートル）
const DISTANCE_THRESHOLD_METERS = config.map.distanceThresholdMeters;

// ズーム調整用の定数
const SAFETY_MARGIN = config.map.zoomAdjust.safetyMargin;
const DEFAULT_PADDING = config.map.zoomAdjust.padding;
const ADJUST_MIN_ZOOM = config.map.zoomAdjust.adjustMinZoom;
const ADJUST_MAX_ZOOM = config.map.zoomAdjust.adjustMaxZoom;
const TILE_SIZE = config.map.tileSize;

const DEFAULT_ZOOM_LEVEL = config.map.zoomLevel.defaultZoomLevel;
const PREF_ZOOM_LEVEL = config.map.zoomLevel.prefZoomLevel;
const CITY_ZOOM_LEVEL = config.map.zoomLevel.cityZoomLevel;
const STATION_ZOOM_LEVEL = config.map.zoomLevel.stationZoomLevel;
const AIRPORT_ZOOM_LEVEL = config.map.zoomLevel.airportZoomLevel;

/**
 * 地図コンポーネント
 */
const Map = ({
  style,
  styleName,
  center,
  zoom,
  pitch,
  bearing,
  mode,
  onMapInitialized,
  onStyleChange,
  onMoveEnd,
  isLayerPopupOpen,
  setIsLayerPopupOpen,
  isPrintPopupOpen,
  setIsPrintPopupOpen,
  handleNormalPrint,
  handleSuperPrint,
  isCenterIndicatorEnabled,
  handleToggleCenterIndicator,
  currentLocationButtonState,
  setCurrentLocationButtonState,
  handlePitchChange,
  handleBearingChange,
  is3dMode,
  setIs3dMode,
  on3dModeToggle,
  onMenuOpen,
  isSearchModalOpen,
  onOpenSearchModal,
  onCloseSearchModal,
  isCategorySearchModalOpen,
  onOpenCategorySearchModal,
  onCloseCategorySearchModal,
  isSearchResultOpen,
  setIsSearchResultOpen,
  isSearchResultVisible,
  setIsSearchResultVisible,
  searchResult,
  setSearchResult,
  searchQuery,
  setSearchQuery,
  selectedIndex,
  setSelectedIndex,
  handleSearch,
  handleSuggestionSelect,
  handleCategorySelection,
  showNotification,
  setLastSearchTerm,
  isLongPressPopupOpen,
  setIsLongPressPopupOpen,
  longPressPosition,
  setLongPressPosition,
  setHasMapMoved,
  hoveredIndex,
  setHoveredIndex,
  setIsResearchHereEnabled,
  selectedCategory,
  setSelectedCategory,
  isShowNavi,
  setIsShowNavi,
  naviSearch,
  setNaviSearch,
  isTablet,
  isMobileDevice,
  setIsPrintMapSuperOpen,

}) => {
  // ========================================
  // 1. Refs
  // ========================================

  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const markersRef = useRef([]);
  // 長押し検知用のstate
  const pressStartTime = useRef(null);
  const pressTimeout = useRef(null);
  const isMoving = useRef(false);
  const moveTimeout = useRef(null);

  // マルチタッチ操作中かどうかのフラグ
  const isMultiTouching = useRef(false);
  const activePointerIds = useRef(new Set());
  const naviRef = useRef();
  const saveSpotRef = useRef();

  // ========================================
  // 2. Local State
  // ========================================

  // 画面ロード時のローディング表示制御用
  // 地図初期化やスタイル変更時にsetIsLoading(true/false), setLoadingProgress(%)を使う
  const [isLoading, setIsLoading] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState(0);

  // ========================================
  // 3. イベントハンドラ（地図操作関連）
  // ========================================

  // ズームイン処理のハンドラを定義
  const handleZoomIn = useCallback(() => {
    if (mapInstance.current) {
      mapInstance.current.zoomIn();
    }
  }, []);

  // ズームアウト処理のハンドラを定義
  const handleZoomOut = useCallback(() => {
    if (mapInstance.current) {
      mapInstance.current.zoomOut();
    }
  }, []);

  // スタイル変更処理のハンドラ
  const handleStyleChange = useCallback((newStyle) => {
    if (mapInstance.current) {
      if (onStyleChange) {
        onStyleChange(newStyle);
      }
    }
  }, [onStyleChange]);

  // ========================================
  // 4. イベントハンドラ（長押し関連）
  // ========================================

  // handleMouseDown を useCallback でラップ（依存配列に追加するため）
  const handleMouseDown = useCallback((e) => {
    if (!mapInstance.current) return;

    // PointerEvent の場合、タッチ操作のIDを記録
    if (e.pointerType === 'touch') {
      activePointerIds.current.add(e.pointerId);
    }

    // 2本以上の指が触れたらマルチタッチモードをON
    if (activePointerIds.current.size >= 2) {
      isMultiTouching.current = true;
    }

    // マルチタッチ操作中は、いかなる場合も長押しタイマーを開始しない
    if (isMultiTouching.current) {
      if (pressTimeout.current) {
        clearTimeout(pressTimeout.current);
        pressTimeout.current = null;
      }
      return;
    }

    // 地図操作中はスキップ
    if (isMoving.current) return;

    // タッチ操作の場合のみ、スクロールや長押しのデフォルト挙動を抑制
    // PCでのマウスクリック(e.pointerType === 'mouse')では preventDefault しない
    if (e.type === 'touchstart' || (e.type === 'pointerdown' && e.pointerType === 'touch')) {
      // touchstart を passive:false で登録していれば preventDefault が効く
      try {
        e.preventDefault();
      } catch (err) {
      }
    }

    // 時間記録
    pressStartTime.current = Date.now();

    // クライアント座標を統一して取得（mouse / touch / pointer 対応）
    const clientX = (e.clientX != null) ? e.clientX : (e.touches && e.touches[0] && e.touches[0].clientX);
    const clientY = (e.clientY != null) ? e.clientY : (e.touches && e.touches[0] && e.touches[0].clientY);

    // 長押しタイマー設定
    pressTimeout.current = setTimeout(() => {
      // 地図上の座標に変換
      const rect = mapRef.current.getBoundingClientRect();
      const x = clientX - rect.left;
      const y = clientY - rect.top;
      const lngLat = mapInstance.current.unproject([x, y]);
      // 親コンポーネントに通知
      setLongPressPosition({
        lat: lngLat.lat,
        lng: lngLat.lng
      });
      setIsLongPressPopupOpen(true);
    }, LONG_PRESS_TIMER);
  }, [setLongPressPosition, setIsLongPressPopupOpen]);

  // 押下解除時にタイマーをクリアして長押し発火をキャンセルする
  const handleMouseUp = (e) => {

    // PointerEvent の場合、タッチ操作のIDを削除
    if (e.pointerType === 'touch') {
      activePointerIds.current.delete(e.pointerId);
    }

    // 予期せぬイベント(pointercancel)や、マウスが領域外に出た(pointerleave)場合も考慮
    if (e.type === 'pointercancel' || e.type === 'pointerleave') {
      activePointerIds.current.clear();
    }

    // 最後の指が離れたらマルチタッチモードをOFFにする
    if (activePointerIds.current.size === 0) {
      isMultiTouching.current = false;
    }

    // 指が離れたので、地図操作中フラグをfalseにする
    isMoving.current = false;
    clearTimeout(pressTimeout.current);
    pressTimeout.current = null;
  };

  // 指やマウスが動いたら長押しはキャンセル（スワイプやドラッグと区別するため）
  const handleMouseMove = () => {

    if (pressTimeout.current) {
      clearTimeout(pressTimeout.current);
      pressTimeout.current = null;
    }
    // 地図操作中フラグを立て、短時間のみtrueにする
    isMoving.current = true;

    // 既存のmoveタイマーがあればクリア
    if (moveTimeout.current) {
      clearTimeout(moveTimeout.current);
    }
    // 100ms後に操作がなければ isMoving を false に戻す
    moveTimeout.current = setTimeout(() => {
      isMoving.current = false;
    }, MOVING_RESET_DELAY_MS);
  };

  // 長押しポップアップを閉じる処理
  const handleCloseLongPress = useCallback(() => {
    // 親コンポーネントに通知
    setLongPressPosition(null);
    setIsLongPressPopupOpen(false);
  }, [setLongPressPosition, setIsLongPressPopupOpen]);

  // ========================================
  // 5. イベントハンドラ（検索結果関連）
  // ========================================

  // 検索結果を閉じる時の処理
  const handleCloseSearchResult = () => {
    setIsSearchResultOpen(false);
    setSearchQuery(''); // 検索ボックスのワードもクリア
    setLastSearchTerm(''); // 最後の検索ワードもクリア

    // マーカーを全て削除
    markersRef.current.forEach(marker => {
      try {
        // ポップアップを削除
        if (marker.__popup) {
          marker.__popup.remove();
        }
        // マーカーを削除
        if (marker.remove) {
          marker.remove();
        }
      } catch (e) {
      }
    });
    markersRef.current = [];

    // ホバー状態とインデックスをリセット
    setHoveredIndex(null);
    setSelectedIndex(null);

    // カテゴリ選択解除
    setSelectedCategory(null);
  };

  // 検索結果選択時の処理
  const handleResultSelect = (item) => {
    // 検索結果から選択されたインデックスを取得
    const items = searchResult.data?.items || [];
    const selectedIdx = items.findIndex(i => i === item);

    // インデックスのみを更新（searchResultは変更しない）
    setSelectedIndex(selectedIdx);
  };

  // 1件選択から複数件表示に戻る際の処理
  const handleSelectMultiple = () => {
    // インデックスをリセットするだけ（searchResult は変更しない）
    setSelectedIndex(null);
  };

  // naviの表示非表示
  const naviToggle = useCallback((bool) => {
    let { current: { naviFromToggle } } = naviRef;
    let isNaviShow = false;
    if (typeof bool === "boolean") {
      isNaviShow = bool;
    } else {
      isNaviShow = !isShowNavi;
    }

    if (isNaviShow) { // naviが表示状態になる時は
      handleCloseSearchResult(); // 検索結果パネルを閉じる
      // TODO: 検索文字も検索結果も全部吹っ飛んでしまうので、ちょっと工夫が必要かな?
      setIsPrintMapSuperOpen(false); // 超印刷をとじる
    }

    naviFromToggle(isNaviShow);
  }, [isShowNavi, handleCloseSearchResult]);

  // ルートの設定
  const setNaviRoute = useCallback(({ name, lat, lon }) => {
    let { current: { setNaviRoute } } = naviRef;
    handleCloseSearchResult(); // naviを表示するのでワード検索結果パネルを閉じる
    setIsPrintMapSuperOpen(false); // 超印刷をとじる
    setNaviRoute({ name, lat, lon });
  });

  const saveSpot = useCallback((spotData) => {
    let { current: { savePOISpot } } = saveSpotRef;
    savePOISpot(spotData);
  });


  // ========================================
  // 6. useEffect - 地図初期化（初回のみ）
  // ========================================

  // 地図の初期化
  useEffect(() => {
    // 初期化開始時にローディングバー表示
    setIsLoading(true);
    try {
      if (mapRef.current) {
        const { map } = createMap(mapRef.current, {
          style,
          center,
          zoom,
          pitch,
          bearing,
          mode
        });

        if (map) {
          mapInstance.current = map;

          // モバイル端末では回転操作だけ無効化する
          // 回転操作が原因で白画面化する事象の回避を目的とする
          // ピンチズームは維持する
          if (isMobileDevice) {
            try {
              // タッチ操作のズーム/回転ジェスチャを扱うハンドラで、回転だけ無効
              map.touchZoomRotate?.disableRotation?.();
              // ドラッグによる回転操作を扱うハンドラ、同様に無効
              map.dragRotate?.disable?.();
            } catch (e) {
            }
          }

          if (onMapInitialized) {
            onMapInitialized(map);
          }

          // 地図の状態をパラメータとして渡す
          if (onMoveEnd) {
            map.on('moveend', (event) => {
              const center = map.getCenter();
              const zoom = map.getZoom();
              const pitch = map.getPitch();
              const bearing = map.getBearing();
              // ユーザー操作による移動かどうか
              const isUserTriggered = !!event?.originalEvent;

              // 地図の状態をオブジェクトとしてonMoveEndに渡す
              onMoveEnd({
                center: { lat: center.lat, lng: center.lng },
                zoom,
                pitch,
                bearing,
                isUserTriggered
              });
            });
          }

          // 地図のタイル読み込み完了時にローディングバー非表示
          map.on('idle', () => {
            setLoadingProgress(LOADING_PROGRESS_COMPLETE);
            setTimeout(() => {
              setIsLoading(false);
            }, LOADING_HIDE_DELAY_MS);
          });

        }
      }
    } catch (error) {
    }

    // クリーンアップ関数
    return () => {
      // マーカーとポップアップを全て削除
      markersRef.current.forEach(marker => {
        try {
          if (marker.__popup) {
            marker.__popup.remove();
          }
          if (marker.remove) {
            marker.remove();
          }
        } catch (e) { }
      });
      markersRef.current = [];

      if (mapInstance.current) {
        // マップインスタンスの破棄
        mapInstance.current.remove();
        mapInstance.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // 初回のみ実行するため依存配列を空にしている（props の変更で再初期化しない）

  // ========================================
  // 7. useEffect - スタイル変更
  // ========================================

  // レイヤースタイルが変更された時の処理
  useEffect(() => {
    if (mapInstance.current && style) {
      // スタイル変更開始時にローディングバー表示
      setIsLoading(true);
      mapInstance.current.setStyle(style);

      // スタイル適用後にローディングバー非表示
      mapInstance.current.once('idle', () => {
        setIsLoading(false);
        setLoadingProgress(100);
      });
    }
  }, [style]);

  // ========================================
  // 8. useEffect - 地図イベント監視（pitch/bearing）
  // ========================================

  // 傾きイベントの監視と処理
  useEffect(() => {
    if (!mapInstance.current) return;

    const map = mapInstance.current;

    const handlePitch = () => {
      if (handlePitchChange) {
        handlePitchChange(map.getPitch());
      }
    };

    map.on('pitch', handlePitch);

    return () => {
      if (map && map.off) {
        map.off('pitch', handlePitch);
      }
    };
  }, [handlePitchChange]);

  // 回転イベントの監視と処理
  useEffect(() => {
    if (!mapInstance.current) return;

    const map = mapInstance.current;

    const handleRotate = () => {
      if (handleBearingChange) {
        handleBearingChange(map.getBearing());
      }
    };

    map.on('rotate', handleRotate);

    return () => {
      if (map && map.off) {
        map.off('rotate', handleRotate);
      }
    };
  }, [handleBearingChange]);

  // ========================================
  // 9. useEffect - マウス/タッチイベント登録
  // ========================================

  // マウス/タッチイベントリスナーの設定
  useEffect(() => {
    if (!mapRef.current) return;
    const mapElem = mapRef.current;
    const supportsPointer = window.PointerEvent != null;

    // PointerEvent がサポートされている場合は pointer 系イベントを優先して登録（mouse と touch を統一して扱える）
    if (supportsPointer) {
      mapElem.addEventListener('pointerdown', handleMouseDown);
      mapElem.addEventListener('pointerup', handleMouseUp);
      mapElem.addEventListener('pointermove', handleMouseMove);
      mapElem.addEventListener('pointercancel', handleMouseUp);
      mapElem.addEventListener('pointerleave', handleMouseUp);
    } else {
      // PointerEvent が無い環境はマウスとタッチを別々に登録
      // デスクトップ向け
      mapElem.addEventListener('mousedown', handleMouseDown);
      mapElem.addEventListener('mouseup', handleMouseUp);
      mapElem.addEventListener('mousemove', handleMouseMove);
      mapElem.addEventListener('mouseleave', handleMouseUp);
      // タッチは passive:false で登録して preventDefault が効くようにする（長押し検知のため）
      mapElem.addEventListener('touchstart', handleMouseDown, { passive: false });
      mapElem.addEventListener('touchend', handleMouseUp, { passive: true });
      mapElem.addEventListener('touchmove', handleMouseMove, { passive: true });
    }

    // クリーンアップ：登録したイベントを確実に解除する
    return () => {
      if (supportsPointer) {
        mapElem.removeEventListener('pointerdown', handleMouseDown);
        mapElem.removeEventListener('pointerup', handleMouseUp);
        mapElem.removeEventListener('pointermove', handleMouseMove);
        mapElem.removeEventListener('pointercancel', handleMouseUp);
        mapElem.removeEventListener('pointerleave', handleMouseUp);
      } else {
        mapElem.removeEventListener('mousedown', handleMouseDown);
        mapElem.removeEventListener('mouseup', handleMouseUp);
        mapElem.removeEventListener('mousemove', handleMouseMove);
        mapElem.removeEventListener('mouseleave', handleMouseUp);
        mapElem.removeEventListener('touchstart', handleMouseDown, { passive: false });
        mapElem.removeEventListener('touchend', handleMouseUp, { passive: true });
        mapElem.removeEventListener('touchmove', handleMouseMove, { passive: true });
      }
    };
  }, [handleMouseDown]);

  // ========================================
  // 10. useEffect - 検索結果マーカー生成・地図調整
  // ========================================

  // 検索結果の地図への反映
  useEffect(() => {

    // 古いマーカーとポップアップを全て削除
    markersRef.current.forEach((marker) => {
      try {
        if (marker.__popup) {
          marker.__popup.remove();
        }
        if (marker.remove) {
          marker.remove();
        }
      } catch (e) {
      }
    });
    markersRef.current = [];

    if (!searchResult || !mapInstance.current) {
      return;
    }

    // マーカー表示用のデータ取得し、最大件数にslice
    const originalItems = searchResult.data?.items || [];
    const items = originalItems.slice(0, MAX_RESULTS);

    // 検索結果0件の場合、早期return
    if (items.length === 0) {
      return;
    }

    const map = mapInstance.current;

    // -------------------------
    // 1件の場合: その地点にセンタリングして適切なズームにする
    // -------------------------
    if (items.length === 1) {
      const first = items[0];
      if (first.lon && first.lat) {
        let zoomLevel = DEFAULT_ZOOM_LEVEL;

        // 検索結果タイプに応じたズーム設定
        const resultType = searchResult.type || first.type || 'spot';
        if (resultType === 'pref') {
          zoomLevel = PREF_ZOOM_LEVEL;  // 都道府県
        } else if (resultType === 'city') {
          zoomLevel = CITY_ZOOM_LEVEL; // 市区町村
        } else if (resultType === 'station') {
          zoomLevel = STATION_ZOOM_LEVEL; // 駅
        } else if (resultType === 'airport') {
          zoomLevel = AIRPORT_ZOOM_LEVEL; // 空港
        }
        // 地図移動（センターとズームを変更）
        try {
          // 移動元を見失わないためflyToで移動
          map.flyTo({
            center: [first.lon, first.lat],
            zoom: zoomLevel,
            duration: FLYTO_DURATION_MS
          });
        } catch (e) {
        }
        // 検索結果が1件の場合はそのピンを選択状態にする
        // （後続のマーカー描画処理のselectedIndex判定で、selectedのスタイルが付く）
        setSelectedIndex(0);
      }

    } else {
      // -------------------------
      // 複数件の場合:中心点とズームレベルを決定
      // 遠距離なら、1件目の位置に移動（distanceが取得できない場合、および指定の距離以上の場合）
      // 近距離なら、現在位置を維持
      // いずれの場合も全件が収まるズームレベルを計算
      // -------------------------

      const first = items[0];

      let isLongDistanceSearch = false;
      // distanceがundefinedとなる時は、「キーワード検索」の結果、つまり遠距離検索の結果である
      if (first.distance === null || first.distance === undefined) {
        isLongDistanceSearch = true;
        // 1件目のdistanceが設定値超で離れていたらそれも遠距離と見なす
      } else if (first.distance != null && first.distance > DISTANCE_THRESHOLD_METERS) {
        isLongDistanceSearch = true;
      }

      // 中心点を決定（遠距離なら1件目、近距離なら現在位置）
      let targetCenter;
      if (isLongDistanceSearch) {
        const pts = items.filter(it => it.lon != null && it.lat != null);
        if (pts.length === 0) {
          targetCenter = map.getCenter();
        } else if (pts.length === 1) {
          targetCenter = { lng: pts[0].lon, lat: pts[0].lat };
        } else {
          // 有効な座標が複数ある場合:
          // 全件の最西端・最東端・最南端・最北端を求めてバウンディングボックスを作り、その中心を代表点として地図中心に設定
          const lons = pts.map(p => p.lon);
          const lats = pts.map(p => p.lat);
          const west = Math.min(...lons);
          const east = Math.max(...lons);
          const south = Math.min(...lats);
          const north = Math.max(...lats);
          targetCenter = { lng: (west + east) / 2, lat: (south + north) / 2 };
        }
      } else {
        // 近距離検索 → 現在の地図中心を維持
        targetCenter = map.getCenter();
      }

      // 地図コンテナのピクセルサイズ取得（取得できなければウィンドウサイズを代用）
      const container = map.getContainer && map.getContainer();
      const mapWidth = (container && container.clientWidth) || window.innerWidth;
      const mapHeight = (container && container.clientHeight) || window.innerHeight;

      // 全件が収まる推定ズームレベルを計算
      const estimatedZoomLevel = calculateZoomKeepCenter(
        items,
        { lng: targetCenter.lng, lat: targetCenter.lat },
        mapWidth,
        mapHeight,
        DEFAULT_PADDING,
        TILE_SIZE,
        ADJUST_MIN_ZOOM,
        ADJUST_MAX_ZOOM
      );

      try {
        // 初期ズームレベルを決定（安全マージン適用）
        const initialZoom = Math.max(Math.floor(estimatedZoomLevel - SAFETY_MARGIN), ADJUST_MIN_ZOOM);

        // 移動元を見失わないためflyToで移動
        try {
          map.flyTo({
            center: [targetCenter.lng, targetCenter.lat],
            zoom: initialZoom,
            duration: FLYTO_DURATION_MS
          });
        } catch (e) {
        }
      } catch (e) {
      }
    }

    // -------------------------
    // マーカー生成（単一/複数 共通処理）
    // - itemsからマーカーを作り、マウスオーバーでpopupを表示する等のイベント登録を行う
    // - 後でクリーンアップのためにDOMハンドラ参照やpopupをmarkerオブジェクトに保持しておく
    // -------------------------
    markersRef.current = items.map((item, idx) => {
      if (!item.lon || !item.lat) return null;
      const el = document.createElement('div');

      el.className = 'marker';
      // svgの内部構造はcss移植せずjsxに直接記述
      el.innerHTML = `
        <svg class="marker-svg" viewBox="0 0 24 24" width="24" height="24" aria-hidden="true" focusable="false">
          <circle class="marker-outline" cx="12" cy="10" r="7" fill="none" stroke-width="2"></circle>
          <circle class="marker-fill" cx="12" cy="10" r="6"></circle>
          <circle class="marker-inner" cx="12" cy="16" r="2" fill="#FFFFFF"></circle>
        </svg>
      `;

      const marker = new window.maplibregl.Marker(el)
        .setLngLat([item.lon, item.lat])
        .addTo(mapInstance.current);

      // マーカーのマウスオーバー時にポップアップを表示する処理
      // マウスイベントをつける対象取得
      const markerEl = marker.getElement();
      if (markerEl) {
        // ポップアップ要素を作成
        const popup = new window.maplibregl.Popup({
          offset: POPUP_OFFSET,
          closeButton: false
        }).setText(item.name || item.poi_name)
          .setLngLat([item.lon, item.lat]);

        const onMarkerEnter = () => {
          setHoveredIndex(idx);
        };
        const onMarkerLeave = () => {
          setHoveredIndex(null);
        };
        // マーカークリック時は、検索結果パネルから1件選んだ時と同じ処理とする
        const onMarkerClick = () => {
          // インデックスを設定
          setSelectedIndex(idx);
        };

        // イベント登録
        markerEl.addEventListener('mouseenter', onMarkerEnter);
        markerEl.addEventListener('mouseleave', onMarkerLeave);
        markerEl.addEventListener('click', onMarkerClick);

        // 後でクリーンアップできるように参照を保持
        marker.__markerEl = markerEl;
        marker.__onEnter = onMarkerEnter;
        marker.__onLeave = onMarkerLeave;
        marker.__onClick = onMarkerClick;
        marker.__popup = popup;
      }

      return marker;
    }).filter(Boolean);

    // クリーンアップ
    return () => {
      markersRef.current.forEach(marker => {
        try {
          // 登録したDOMイベントとpopup/markerを削除してメモリリークや二重登録を防ぐ
          if (marker.__markerEl) {
            if (marker.__onEnter) {
              marker.__markerEl.removeEventListener('mouseenter', marker.__onEnter);
            }
            if (marker.__onLeave) {
              marker.__markerEl.removeEventListener('mouseleave', marker.__onLeave);
            }
            if (marker.__onClick) {
              marker.__markerEl.removeEventListener('click', marker.__onClick);
            }
          }
          if (marker.__popup) {
            marker.__popup.remove();
          }
          // マーカー本体を削除
          if (marker.remove) {
            marker.remove();
          }
        } catch (err) {
        }
      });
      markersRef.current = [];
    };
  }, [searchResult, setHoveredIndex, setSelectedIndex]);

  // ========================================
  // 11. useEffect - マーカーホバー/選択状態管理
  // ========================================

  // 検索結果/地図上のマーカーのマウスオーバー時の処理
  useEffect(() => {

    // 全てのポップアップを一旦削除（残留ポップアップを防ぐ）
    markersRef.current.forEach((marker) => {
      if (marker.__popup) {
        marker.__popup.remove();
      }
    });

    // hoveredIndex（マウスオーバー中のindex）が変わった時に、当該マーカーのスタイルを変更
    markersRef.current.forEach((marker, idx) => {
      const el = marker.getElement();
      if (!el) return;

      // クラスで状態管理
      // 選択状態のクラス管理（選択されたものは selected クラス）
      el.classList.toggle('marker--selected', selectedIndex === idx);
      // マウスオーバーは、「選択」されていないもののみ適用
      el.classList.toggle('marker--hovered', hoveredIndex === idx && selectedIndex !== idx);

      // hoveredIndex のマーカーのポップアップを表示
      if (marker.__popup && hoveredIndex === idx) {
        try {
          marker.__popup.addTo(mapInstance.current);
        } catch (err) { }
      }

    });
  }, [hoveredIndex, selectedIndex]);

  // ========================================
  // 12. JSX Return
  // ========================================

  return (
    <>
      {/* ローディングバー（地図本体より前に表示） */}
      {isLoading && (
        <div id="loading-bar" className="loading-bar">
          <div
            className="loading-progress"
            style={{ width: `${loadingProgress}%` }} />
        </div>
      )}

      {/* 地図表示エリア */}
      <div id="map" ref={mapRef}>
        {/* 地図本体（MapLibreが描画する） */}

        {/* 中心点表示 */}
        {isCenterIndicatorEnabled && (
          <div id="map-center-indicator" />
        )}
      </div>

      {/* ここにボタンやスケールバーや著作権（mapInstance.currentが準備できたら表示） */}
      {
        mapInstance.current && (
          <>
            <ScaleBar map={mapInstance.current} />

            {/* 著作権表示 */}
            <CopyrightNotice />

            {/* 右下ボタン群 */}
            <MapControls
              map={mapInstance.current}
              mapRef={mapRef}
              onZoomInClick={handleZoomIn}
              onZoomOutClick={handleZoomOut}
              onStyleChange={handleStyleChange}
              currentStyleName={styleName}
              setHasMapMoved={setHasMapMoved}
              // レイヤーポップアップの表示状態と更新関数
              isLayerPopupOpen={isLayerPopupOpen}
              setIsLayerPopupOpen={setIsLayerPopupOpen}
              // 印刷ポップアップの表示状態と更新関数
              isPrintPopupOpen={isPrintPopupOpen}
              setIsPrintPopupOpen={setIsPrintPopupOpen}
              handleNormalPrint={handleNormalPrint}
              handleSuperPrint={handleSuperPrint}
              // 現在地ボタンの状態と更新関数
              currentLocationButtonState={currentLocationButtonState}
              setCurrentLocationButtonState={setCurrentLocationButtonState}
              // 傾き変更時の状態と更新関数（3Dボタン表示判定に使用）
              pitch={pitch}
              handlePitchChange={handlePitchChange}
              // 方位変更時の状態と更新関数（コンパスボタン表示判定に使用）
              bearing={bearing}
              handleBearingChange={handleBearingChange}
              // 3Dモードの状態と更新関数
              is3dMode={is3dMode}
              setIs3dMode={setIs3dMode}
              on3dModeToggle={on3dModeToggle}
              isShowNavi={isShowNavi}
            />

            {/* 左下ボタン群 */}
            <MapLeftControls
              // フルスクリーン
              mapRef={mapRef}
            />

            {/* 右クリックポップアップ */}
            <RightClickPopup
              map={mapInstance.current}
              // 印刷ポップアップの表示状態とその更新関数を渡す
              isPrintPopupOpen={isPrintPopupOpen}
              setIsPrintPopupOpen={setIsPrintPopupOpen}
              handleNormalPrint={handleNormalPrint}
              handleSuperPrint={handleSuperPrint}
              // 中心点表示ON/OFFの状態とその更新関数を渡す
              isCenterIndicatorEnabled={isCenterIndicatorEnabled}
              handleToggleCenterIndicator={handleToggleCenterIndicator}
              // ナビのルート設定
              setNaviRoute={setNaviRoute}
              // スポットの保存
              saveSpot={saveSpot}
            />

            {/* 長押しポップアップ */}
            <LongPressPopup
              position={longPressPosition}
              isLongPressPopupOpen={isLongPressPopupOpen}
              onCloseLongPressPopup={handleCloseLongPress}
              map={mapInstance.current}
              // ナビのルート設定
              setNaviRoute={setNaviRoute}
              // スポットの保存
              saveSpot={saveSpot}
            />

            {/* 検索パネル */}
            <Search
              map={mapInstance.current}
              isSearchModalOpen={isSearchModalOpen}
              onOpenSearchModal={onOpenSearchModal}
              onCloseSearchModal={onCloseSearchModal}
              isCategorySearchModalOpen={isCategorySearchModalOpen}
              onOpenCategorySearchModal={onOpenCategorySearchModal}
              onCloseCategorySearchModal={onCloseCategorySearchModal}

              handleSearch={handleSearch}
              handleSuggestionSelect={handleSuggestionSelect}
              handleCategorySelection={handleCategorySelection}
              onMenuOpen={onMenuOpen}
              isSearchResultOpen={isSearchResultOpen}
              setIsSearchResultOpen={setIsSearchResultOpen}
              searchResult={searchResult}
              setSearchResult={setSearchResult}
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              selectedIndex={selectedIndex}
              setSelectedIndex={setSelectedIndex}
              showNotification={showNotification}
              selectedCategory={selectedCategory}
              setSelectedCategory={setSelectedCategory}
              setHasMapMoved={setHasMapMoved}
              handleCloseSearchResult={handleCloseSearchResult}
              center={center}
              naviToggle={naviToggle}
              isShowNavi={isShowNavi}

            />

            {/* 検索結果パネル */}
            {isSearchResultOpen && searchResult && (
              <SearchResult
                // 検索結果データ
                result={searchResult}
                // 検索クエリ
                searchQuery={searchQuery}
                // 閉じるボタン用コールバック
                handleCloseSearchResult={handleCloseSearchResult}
                // 検索結果選択時のコールバック
                handleResultSelect={handleResultSelect}
                // 選択中インデックス
                selectedIndex={selectedIndex}
                // 選択中インデックスを更新
                setSelectedIndex={setSelectedIndex}
                // 検索結果パネルの一時表示状態
                isSearchResultVisible={isSearchResultVisible}
                // 検索結果パネルの一時表示状態を更新
                setIsSearchResultVisible={setIsSearchResultVisible}
                // マウスオーバー中インデックス
                hoveredIndex={hoveredIndex}
                // マウスオーバー中インデックスを更新
                setHoveredIndex={setHoveredIndex}
                // 複数選択時のコールバック
                handleSelectMultiple={handleSelectMultiple}
                // 「この辺で検索」ボタン状態を更新
                setIsResearchHereEnabled={setIsResearchHereEnabled}
                // モバイル・タブレット判定
                isTablet={isTablet}
                // ナビのルート設定
                setNaviRoute={setNaviRoute}
                // スポットの保存
                saveSpot={saveSpot}
              />
            )}

            <NaviComponent
              map={mapInstance.current}
              isShowNavi={isShowNavi}
              setIsShowNavi={setIsShowNavi}
              naviToggle={naviToggle}
              naviSearch={naviSearch}
              setNaviSearch={setNaviSearch}
              isSearchResultOpen={isSearchResultOpen}
              ref={naviRef}
              isTablet={isTablet}
              style={style}
            />

            {/* スポット保存用の非表示コンポーネント */}
            <SaveSpot
              ref={saveSpotRef}
              isTablet={isTablet}
            />

          </>

        )
      }
    </>
  );
};

export default Map;